/**Import react section */
import React, { Fragment } from 'react'
import propTypes from 'prop-types'

/** Material UI import section */
import { Button, DialogContent, Typography } from '@material-ui/core'
import { ButtonCreateStyles } from '../../../resources/styles/ButtonCreateStyles'

/** Custom components import section */
import Dialog from '../../common/Dialog'
import AlertComponent from '../../common/AlertComponent'

/**
 * Confirmation dialog shown before persisting an inline field change from the services listing.
 * No password confirmation is required for these operations.
 *
 * The field is named through a prop rather than baked into the copy: the listing has more than one
 * column that captures a value in place, and two dialogs identical but for a noun drift apart the
 * moment someone fixes a detail in only one of them.
 * @param {*} props
 * @returns
 */
const ConfirmFieldUpdateDialog = (props) => {
	const classes = ButtonCreateStyles()
	const { open, onClose, onConfirm, isSaving, ticket, newValueLabel, fieldName } = props

	/**
	 * Material UI's Dialog renders through a Portal, but React's synthetic events still bubble
	 * through the React component tree (not the DOM tree) — without this, a click anywhere in the
	 * dialog (backdrop, close button, CONFIRM) bubbles up to the grid row's onClick and navigates
	 * to the service detail. Stopping propagation here, as the React ancestor of the portal content,
	 * blocks that bubbling regardless of where in the dialog the click originated.
	 */
	return (
		<div onClick={(event) => event.stopPropagation()}>
			<Dialog
				open={open}
				onClose={onClose}
				isSaving={isSaving}
				header={<strong>Confirm operation</strong>}
				actions={
					<Fragment>
						<Button variant='contained' className={classes.btnSaved} disabled={isSaving} onClick={onConfirm}>
							CONFIRM
						</Button>
					</Fragment>
				}
			>
				<DialogContent>
					<AlertComponent title={'Warning'} message={`This operation changes the ${fieldName} value.`} severityType={'warning'} />
					<br />
					<Typography variant='subtitle2'>
						{`Are you sure you want to change the ${fieldName} value of ticket ${ticket} to "${newValueLabel}"?`}
					</Typography>
					<br />
				</DialogContent>
			</Dialog>
		</div>
	)
}

ConfirmFieldUpdateDialog.propTypes = {
	/**Determines if the dialog is open */
	open: propTypes.bool,
	/**Handles closing/cancelling the dialog */
	onClose: propTypes.func.isRequired,
	/**Triggers the confirmed change */
	onConfirm: propTypes.func.isRequired,
	/**Determines if the update is currently being saved */
	isSaving: propTypes.bool,
	/**The service ticket number, shown in the confirmation message */
	ticket: propTypes.any,
	/**The label of the value being confirmed */
	newValueLabel: propTypes.string,
	/**The name of the field being changed, as the user reads it in the column header */
	fieldName: propTypes.string.isRequired
}

// `onClose` and `onConfirm` are deliberately absent: React applies defaultProps before validating
// propTypes, so giving a required callback a default means the validator never sees `undefined` and
// the isRequired warning never fires. A consumer that forgot one would learn about it from a
// console.warn on click instead of from a warning in development.
ConfirmFieldUpdateDialog.defaultProps = {
	open: false,
	isSaving: false
}

export default ConfirmFieldUpdateDialog
